import React, { useState, useEffect, useCallback } from 'react';
import { ShoppingBag } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ModuleAlertsPage from '../../shared/components/ModuleAlertsPage';
import localApiAdapter from '../../core/services/localApiAdapter';
import { isAchatsCatalogLowStock } from './achatsListPageUtils';
import { useAchatsConfig } from './useAchatsConfig';

const isPending = (status) => status === 'pending';

const AchatsAlertsPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`modules.achats.pages.achatsAlertsPage.${key}`, opts);
  const achatsConfig = useAchatsConfig();

  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAlerts = useCallback(async () => {
    try {
      setLoading(true);
      const requests = [
        localApiAdapter.get('/achats/purchase-orders'),
        localApiAdapter.get('/achats/demandes'),
        localApiAdapter.get('/achats/receptions'),
      ];
      if (achatsConfig.lowStockAlertEnabled) {
        requests.push(localApiAdapter.get('/achats/catalogue-fournisseurs'));
      }

      const results = await Promise.all(requests);
      const orders = results[0]?.data || results[0] || [];
      const purchaseRequests = results[1]?.data || results[1] || [];
      const receptions = results[2]?.data || results[2] || [];
      const catalogItems = achatsConfig.lowStockAlertEnabled
        ? (results[3]?.data || results[3] || [])
        : [];

      const pendingOrders = orders.filter((o) => isPending(o.status));
      const pendingRequests = purchaseRequests.filter((r) => isPending(r.status));
      const recentReceptions = receptions.slice(0, 5);
      const lowStockItems = catalogItems.filter(isAchatsCatalogLowStock);

      const mapped = [
        ...pendingOrders.map((order) => ({
          id: `order-${order.id}`,
          type: 'warning',
          priority: 'high',
          title: p('alert_late_orders_title'),
          message: p('alert_late_orders_message', {
            number: order.order_number || order.id,
            supplier: order.supplier_name || '',
          }),
          date: (order.order_date || order.created_at || '').split('T')[0],
          read: false,
          amount: Number(order.total_amount) || undefined,
        })),
        ...pendingRequests.map((request) => ({
          id: `request-${request.id}`,
          type: 'info',
          priority: 'medium',
          title: p('alert_requests_title'),
          message: p('alert_requests_message', {
            number: request.request_number || request.id,
          }),
          date: (request.request_date || request.created_at || '').split('T')[0],
          read: false,
        })),
        ...(achatsConfig.lowStockAlertEnabled
          ? lowStockItems.map((item) => ({
              id: `lowstock-${item.id}`,
              type: 'warning',
              priority: 'high',
              title: p('alert_low_stock_title', { defaultValue: 'مخزون منخفض في فهرس الموردين' }),
              message: p('alert_low_stock_message', {
                defaultValue: '{{product}} — المخزون {{stock}} (حد إعادة الطلب {{reorder}})',
                product: item.product_name || item.catalog_code || item.id,
                stock: item.stock_quantity ?? 0,
                reorder: item.reorder_level ?? 0,
              }),
              date: (item.updated_at || item.created_at || '').split('T')[0],
              read: false,
            }))
          : []),
        ...recentReceptions.map((reception) => ({
          id: `reception-${reception.id}`,
          type: 'success',
          priority: 'low',
          title: p('alert_reception_title'),
          message: p('alert_reception_message', {
            number: reception.receipt_number || reception.id,
          }),
          date: (reception.receipt_date || reception.created_at || '').split('T')[0],
          read: true,
          amount: Number(reception.total_amount) || undefined,
        })),
      ];

      setAlerts(mapped);
    } catch (error) {
      console.error('Error loading achats alerts:', error);
      setAlerts([]);
    } finally {
      setLoading(false);
    }
  }, [achatsConfig.lowStockAlertEnabled, p]);

  useEffect(() => {
    void loadAlerts();
  }, [loadAlerts]);

  return (
    <ModuleAlertsPage
      moduleName={p('module_name')}
      moduleIcon={ShoppingBag}
      alerts={alerts}
      loading={loading}
    />
  );
};

export default AchatsAlertsPage;
